import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { FileText, ClipboardList, Save, PenLine, Lock, CheckCircle2 } from "lucide-react";
import DocumentsVivier from "../components/vivier/DocumentsVivier";
import SignaturePad from "../components/vivier/SignaturePad";
import { API, JOURS, L_POSTE, L_STATUT, dateFr, erreur, euros, nomComplet, telecharger } from "../components/vivier/outils";

const CHAMPS = ["telephone", "email", "adresse", "ville", "zone", "jours_dispo",
  "date_naissance", "numero_ss", "siret", "raison_sociale", "assurance_rc"];

// Espace d'un extra : sa fiche, ses missions (tous food trucks confondus),
// la signature de ses contrats et ses pieces. Rien sur les autres extras.
export default function MonEspaceExtraPage() {
  const { user } = useAuth();
  const [fiche, setFiche] = useState(null);
  const [charge, setCharge] = useState(false);
  const [form, setForm] = useState({});
  const [missions, setMissions] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [aSigner, setASigner] = useState(null);
  const [signature, setSignature] = useState("");
  const [accepte, setAccepte] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vivier`);
      const f = (r.data || [])[0] || null;
      setFiche(f);
      if (f) {
        setForm(Object.fromEntries(CHAMPS.map((k) => [k, f[k] ?? (k === "jours_dispo" ? [] : "")])));
        const m = await axios.get(`${API}/vivier/${f.id}/missions`);
        setMissions(m.data);
      }
    } catch (e) {
      toast.error(erreur(e, "Chargement impossible"));
    } finally {
      setCharge(true);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async () => {
    setEnCours(true);
    try {
      const r = await axios.put(`${API}/vivier/${fiche.id}`, form);
      setFiche(r.data);
      toast.success("Fiche mise à jour");
    } catch (e) {
      toast.error(erreur(e, "Enregistrement impossible"));
    } finally {
      setEnCours(false);
    }
  };

  const signer = async () => {
    try {
      await axios.post(`${API}/vivier/missions/${aSigner.id}/signer`, { signature, accepte });
      toast.success("Contrat signé. Vous pouvez le télécharger.");
      setASigner(null);
      charger();
    } catch (e) {
      toast.error(erreur(e, "Signature impossible"));
    }
  };

  const basculeJour = (j) => setForm((f) => ({
    ...f,
    jours_dispo: (f.jours_dispo || []).includes(j) ? f.jours_dispo.filter((x) => x !== j) : [...(f.jours_dispo || []), j],
  }));

  if (!charge) return null;
  if (!fiche) {
    return (
      <Card><CardContent className="p-8 text-center text-muted-foreground">
        Aucune fiche n'est encore reliée à votre compte ({user?.email}). Contactez le bureau de l'association.
      </CardContent></Card>
    );
  }

  const ae = fiche.statut === "auto_entrepreneur";
  const champ = (k, label, props = {}) => (
    <div><Label>{label}</Label>
      <Input value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} {...props} /></div>
  );
  const nonSignees = (missions?.missions || []).filter((m) => !m.signe_le).length;

  return (
    <div className="space-y-4" data-testid="mon-espace-extra">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary">Mon espace extra</h1>
          <p className="text-sm text-muted-foreground">
            {nomComplet(fiche)}{fiche.statut ? ` · ${L_STATUT[fiche.statut] || fiche.statut}` : ""}
          </p>
        </div>
        <DocumentsVivier personne={fiche} libelle="Mes pièces" />
      </div>

      {nonSignees > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">
            {nonSignees === 1 ? "Une mission attend" : `${nonSignees} missions attendent`} la signature de votre contrat.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Mes missions</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {missions && (
            <div className="flex flex-wrap gap-4 text-sm">
              <span>Total : <strong>{String(missions.total_heures).replace(".", ",")} h</strong></span>
              <span>Montant : <strong>{euros(missions.total_montant)}</strong></span>
              <span>Reste à percevoir : <strong>{euros(missions.reste_a_payer)}</strong></span>
            </div>
          )}
          {(missions?.missions || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune mission pour le moment.</p>
          ) : missions.missions.map((m) => (
            <div key={m.id} className="flex items-center gap-2 text-sm border rounded-md p-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{dateFr(m.date)} · {m.food_truck_nom}</p>
                <p className="text-xs text-muted-foreground">
                  {m.lieu || "Lieu communiqué par le food truck"} · {m.heure_debut} à {m.heure_fin} · {String(m.heures).replace(".", ",")} h
                  {m.paye ? " · payée" : ""}
                </p>
              </div>
              <span className="whitespace-nowrap font-medium">{euros(m.montant)}</span>
              {m.signe_le ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Signé
                </Badge>
              ) : (
                <Button size="sm" className="h-7 text-xs" onClick={() => { setASigner(m); setSignature(""); setAccepte(false); }}>
                  <PenLine className="w-3.5 h-3.5 mr-1" /> Lire et signer
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Télécharger le contrat"
                onClick={() => telecharger(`/vivier/${fiche.id}/contrat`, `contrat_${m.date}.pdf`, { mission_id: m.id })}>
                <FileText className="w-3.5 h-3.5" />
              </Button>
              {ae && (
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Bon de commande"
                  onClick={() => telecharger(`/vivier/${fiche.id}/bon-commande`, `bon_commande_${m.date}.pdf`, { mission_id: m.id })}>
                  <ClipboardList className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{ae ? "Tarif horaire facturé" : "Taux horaire indicatif"}</span>
            <strong>{euros(ae && fiche.tarif_horaire ? fiche.tarif_horaire : fiche.taux_horaire)} / h</strong>
          </div>
          {(fiche.postes || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {fiche.postes.map((x) => <Badge key={x} variant="outline" className="text-[10px]">{L_POSTE[x] || x}</Badge>)}
            </div>
          )}
          <p className="text-xs text-muted-foreground w-full">
            Le taux de chaque mission est fixé par le food truck qui vous emploie et figure sur votre contrat.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Mes coordonnées et disponibilités</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {champ("telephone", "Téléphone")}
            {champ("email", "Email", { type: "email" })}
            {champ("ville", "Ville")}
            {champ("zone", "Zone où vous pouvez travailler", { placeholder: "Saclay, Massy..." })}
          </div>
          {champ("adresse", "Adresse complète")}
          <div><Label>Jours disponibles</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {JOURS.map((j) => (
                <Button key={j} type="button" size="sm" variant={(form.jours_dispo || []).includes(j) ? "default" : "outline"}
                  onClick={() => basculeJour(j)}>{j.slice(0, 3)}</Button>
              ))}
            </div>
          </div>
          <p className="text-sm font-medium pt-2">Pour vos contrats</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {champ("date_naissance", "Date de naissance", { type: "date" })}
            {champ("numero_ss", "N° de sécurité sociale")}
            {ae && champ("siret", "SIRET")}
            {ae && champ("raison_sociale", "Raison sociale")}
            {ae && champ("assurance_rc", "Assurance RC pro (n° de police)")}
          </div>
          <p className="text-xs text-muted-foreground">
            Ces données ne sont visibles que par le bureau et imprimées sur les contrats des food trucks qui vous emploient.
          </p>
          <Button onClick={enregistrer} disabled={enCours}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
        </CardContent>
      </Card>

      <Dialog open={!!aSigner} onOpenChange={(o) => { if (!o) setASigner(null); }}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Signer mon contrat</DialogTitle></DialogHeader>
          {aSigner && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border p-3 bg-muted/30 space-y-1">
                <p><strong>Employeur :</strong> {aSigner.food_truck_nom}</p>
                <p><strong>Date :</strong> {dateFr(aSigner.date)} · {aSigner.heure_debut} à {aSigner.heure_fin}</p>
                <p><strong>Lieu :</strong> {aSigner.lieu || "communiqué par le food truck"}</p>
                <p><strong>Rémunération :</strong> {euros(aSigner.taux_horaire)} / h, soit {euros(aSigner.montant)} pour {String(aSigner.heures).replace(".", ",")} h</p>
              </div>
              <Button variant="outline" size="sm"
                onClick={() => telecharger(`/vivier/${fiche.id}/contrat`, `contrat_${aSigner.date}.pdf`, { mission_id: aSigner.id })}>
                <FileText className="w-3.5 h-3.5 mr-1" /> Lire le contrat complet (PDF)
              </Button>
              <SignaturePad onChange={setSignature} />
              <label className="flex items-start gap-2">
                <input type="checkbox" className="mt-1" checked={accepte} onChange={(e) => setAccepte(e.target.checked)} />
                <span>J'ai lu et j'accepte le contrat. Ma signature électronique a la même valeur que ma signature manuscrite.</span>
              </label>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setASigner(null)}>Annuler</Button>
            <Button onClick={signer} disabled={!signature || !accepte}>Signer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
